// A skill check that is shown to the player as a die in the chat, instead of only deciding the outcome behind the scenes.
// It is deliberately rare: routine checks stay invisible so the chat doesn't turn into a dice game.

export interface DiceRoll {
  /** persuasion: talking someone round; check: any other stat check; consideration: the one die for a risky moment with her. */
  kind: "persuasion" | "check" | "consideration"
  statLabel: string
  statValue: number
  difficulty: number
  /** Percent chance of success (5-95). The roll succeeds when roll <= chance. */
  chance: number
  /** The die: 1-100. */
  roll: number
  succeeded: boolean
  description: string
  /** Manual mode: the game is waiting for the player to press Roll. */
  pending?: boolean
}

export const HIGH_STAKES_DIFFICULTY = 70

/**
 * A roll is shown only when it matters: talking round someone who is actively resisting (an open argument or low
 * morale), or an attempt that is genuinely hard. Everything else stays behind the scenes.
 */
export function shouldShowDice(roll: Pick<DiceRoll, "kind" | "difficulty">, resistance: number): boolean {
  if (roll.kind === "persuasion" && resistance > 0) return true
  return roll.difficulty >= HIGH_STAKES_DIFFICULTY
}

/** Rolls the die against a success chance. Pure apart from the random source, which can be injected for tests. */
export function rollDie(chance: number, random: () => number = Math.random): { roll: number; succeeded: boolean } {
  const roll = 1 + Math.floor(random() * 100)
  return { roll, succeeded: roll <= chance }
}

/** Defensive parse of a stored dice_roll value; anything malformed is dropped rather than rendered. */
export function parseDiceRoll(raw: unknown): DiceRoll | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null)
  const roll = num(r.roll), chance = num(r.chance), difficulty = num(r.difficulty), statValue = num(r.statValue)
  if (roll === null || chance === null || difficulty === null || statValue === null) return null
  if (typeof r.statLabel !== "string" || typeof r.succeeded !== "boolean") return null
  return {
    kind: r.kind === "persuasion" ? "persuasion" : r.kind === "consideration" ? "consideration" : "check",
    statLabel: r.statLabel.slice(0, 40),
    statValue, difficulty, chance, roll,
    succeeded: r.succeeded,
    description: typeof r.description === "string" ? r.description.slice(0, 140) : "",
    ...(r.pending === true ? { pending: true } : {}),
  }
}
